import { Component, Structure } from '@wix/thunderbolt-becky-types'
import * as _ from 'lodash'
import { PINNED_LAYER_SUFFIX } from '../constants'
import {
	checkIfPinnedComponentAndAddToMap,
	createComponent,
	isFixed,
	PinnedPositions,
} from './utils/pinnedComponentsUtils'
import {
	ComponentsByType,
	createPinnedGroup,
	createSOAPGroup,
	createStructuralGroup,
	getIsStructural,
	GROUP_TYPES,
	MasterPageGroup,
	PINNED_GROUPS_IDS,
} from './utils/masterPageUtils'

const sortMasterPageChildrenIntoGroups = (
	childrenIds: Array<string>,
	childrenLayout: { [compId: string]: Component['layout'] },
	isMobileView: boolean
): ComponentsByType => {
	const isStructural = getIsStructural({ isMobileView })
	const pagesContainerIdx = childrenIds.findIndex((childId) => childId === 'PAGES_CONTAINER')

	return childrenIds.reduce(
		(acc, childId, idx) => {
			if (isStructural(childId)) {
				acc.structural.push(childId)
			} else {
				const childLayout = childrenLayout[childId]
				const isPinned = checkIfPinnedComponentAndAddToMap(childId, childLayout, acc.pinned)
				if (!isPinned && !isFixed(childLayout)) {
					acc.soap[idx < pagesContainerIdx ? 'beforeMain' : 'afterMain'].push(childId)
				}
			}

			return acc
		},
		{
			pinned: {},
			soap: {
				beforeMain: [],
				afterMain: [],
			},
			structural: [],
		} as ComponentsByType
	)
}

const getMasterPageChildrenGroups = (
	childrenIds: Array<string>,
	childrenLayout: { [compId: string]: Component['layout'] },
	isMobileView: boolean
): Array<MasterPageGroup> => {
	const componentsByType = sortMasterPageChildrenIntoGroups(childrenIds, childrenLayout, isMobileView)

	const createPinnedGroupIfNotEmpty = (position: PinnedPositions) =>
		componentsByType.pinned[position] &&
		createPinnedGroup(componentsByType.pinned[position]!, PINNED_GROUPS_IDS[position], 'masterPage')

	return [
		createPinnedGroupIfNotEmpty('TOP_LEFT'),
		createPinnedGroupIfNotEmpty('TOP_CENTER'),
		createPinnedGroupIfNotEmpty('TOP_RIGHT'),
		componentsByType.structural.includes('SITE_HEADER') && createStructuralGroup('SITE_HEADER'),
		componentsByType.structural.includes('SOSP_CONTAINER_CUSTOM_ID') &&
			createStructuralGroup('SOSP_CONTAINER_CUSTOM_ID'),
		createPinnedGroupIfNotEmpty('CENTER_LEFT'),
		createPinnedGroupIfNotEmpty('CENTER_RIGHT'),
		componentsByType.soap.beforeMain.length > 0 &&
			createSOAPGroup(componentsByType.soap.beforeMain, 'soapBeforePagesContainer'),
		componentsByType.structural.includes('PAGES_CONTAINER') && createStructuralGroup('PAGES_CONTAINER'),
		componentsByType.soap.afterMain.length > 0 &&
			createSOAPGroup(componentsByType.soap.afterMain, 'soapAfterPagesContainer'),
		createPinnedGroupIfNotEmpty('BOTTOM_LEFT'),
		createPinnedGroupIfNotEmpty('BOTTOM_CENTER'),
		createPinnedGroupIfNotEmpty('BOTTOM_RIGHT'),
		componentsByType.structural.includes('SITE_FOOTER') && createStructuralGroup('SITE_FOOTER'),
		componentsByType.structural.includes('BACK_TO_TOP_BUTTON') && createStructuralGroup('BACK_TO_TOP_BUTTON'),
		componentsByType.structural.includes('QUICK_ACTION_BAR') && createStructuralGroup('QUICK_ACTION_BAR'),
	].filter((group) => group) as Array<MasterPageGroup>
}

const addPinnedGroupToStructure = (structureComps: Structure, group: MasterPageGroup) => {
	const isBeforeHeader = () =>
		[PINNED_GROUPS_IDS.TOP_LEFT, PINNED_GROUPS_IDS.TOP_CENTER, PINNED_GROUPS_IDS.TOP_RIGHT].includes(group.id!)
	const isBeforeFooter = () =>
		[PINNED_GROUPS_IDS.BOTTOM_LEFT, PINNED_GROUPS_IDS.BOTTOM_CENTER, PINNED_GROUPS_IDS.BOTTOM_RIGHT].includes(
			group.id!
		)

	const components: Array<string> = []
	_.forEach(group.components, (id) => {
		const pinnedId = `${id}${PINNED_LAYER_SUFFIX}`
		structureComps[pinnedId] = createComponent(pinnedId, [id], group.id!, 'PinnedLayer', 'Container')
		components.push(pinnedId)
	})

	const groupParentComponents = structureComps[group.parent!].components!
	structureComps[group.id!] = createComponent(group.id!, components, group.parent!, 'DivWithChildren')

	if (isBeforeHeader()) {
		const siteHeaderIndex = groupParentComponents.findIndex((compId) => compId === 'SITE_HEADER')
		groupParentComponents.splice(siteHeaderIndex, 0, group.id!)
	} else if (isBeforeFooter()) {
		const siteFooterIndex = groupParentComponents.findIndex((compId) => compId === 'SITE_FOOTER')
		groupParentComponents.splice(siteFooterIndex, 0, group.id!)
	}
	// Between header and footer
	else {
		const siteHeaderIndex = groupParentComponents.findIndex((compId) => compId === 'SITE_HEADER')
		groupParentComponents.splice(siteHeaderIndex + 1, 0, group.id!)
	}
}

const addGroupedCompsToStructure = (structureComps: Structure, masterPageChildrenGroups: Array<MasterPageGroup>) => {
	_.forEach(masterPageChildrenGroups, (group) => {
		if (group.id) {
			// nonStructuralGroup
			if (group.type === GROUP_TYPES.PINNED) {
				addPinnedGroupToStructure(structureComps, group)
			} else {
				structureComps[group.id] = createComponent(
					group.id,
					group.components,
					'masterPage',
					'MeshGroup',
					'Container'
				)
			}
		}
	})

	return structureComps
}

const convertMasterPageGroupsIntoStructure = (
	masterPage: Structure['masterPage'],
	masterPageGroups: Array<MasterPageGroup>
): Structure => {
	const masterPageChildrenGroups = _.flatMap(masterPageGroups, (childrenGroup) => childrenGroup)

	const nonPinnedMasterPageChildrenGroups = _.filter(
		masterPageChildrenGroups,
		(group) => group.type !== GROUP_TYPES.PINNED
	)
	const masterPageChildren = _.flatMap(nonPinnedMasterPageChildrenGroups, (group) =>
		group.type === GROUP_TYPES.SOAP ? group.id : group.components.slice(0)
	)

	const structureComps = {
		masterPage: { ...masterPage, components: masterPageChildren },
	} as Structure
	return addGroupedCompsToStructure(structureComps, masterPageChildrenGroups)
}

export { getMasterPageChildrenGroups, convertMasterPageGroupsIntoStructure, sortMasterPageChildrenIntoGroups }
